import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiUsers, FiClock, FiBookOpen, FiCheckCircle, FiPlay } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, Page, PageHeader, Card, Stat, Tag, ProgressBar, Alert, EmptyState, Initials, inputStyle, labelStyle, fadeUp, stagger } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/challenge-c5.jpg', pos: 'center 25%', height: 640, opacity: 0.6 };

// Best times saved by the games into localStorage. The keys must match what
// the game pages write — keep them in sync if you rename a game.
const GAME_BEST_TIME_KEYS = [
  { game: "Tim's Clues", key: 'timClues:bestTime' },
  // legacy key written before the Jack → Tim rename
  { game: "Tim's Clues", key: 'jackClues:bestTime' },
];

function formatBestTime(ms) {
  if (!ms || ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}


function firstNameOnly(displayName) {
  if (!displayName) return '—';
  // Show only the first word — never the surname, even if it was saved
  // together with the first name.
  return String(displayName).trim().split(/\s+/)[0];
}

function formatCourse(code) {
  if (!code) return '—';
  // Backend returns the course code; the programme is 'CORE for Schools'.
  if (code === 'TECH1') return 'CORE';
  return code;
}

function formatGameTime(ms) {
  if (!ms || ms <= 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function courseLabel(code) {
  const c = formatCourse(code);
  return c === '6' || c === '7' ? `CORE ${c}` : c;
}

// Minutes spent inside a lesson → friendly label
function formatMinutes(min) {
  const n = Math.round(Number(min) || 0);
  if (n <= 0) return null;
  if (n < 60) return `${n} min`;
  return `${Math.floor(n / 60)} h ${n % 60} min`;
}

// A lesson in progress has no duration here, so grow the bar with the time spent (max 90%).
function lessonPercent(entry) {
  if (entry?.completed) return 100;
  const spent = Number(entry?.timeSpent) || 0;
  return Math.max(10, Math.min(90, 10 + Math.round(spent * 4)));
}

const sectionHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 };
const sectionTitle = { display: 'flex', alignItems: 'center', gap: 10, margin: 0, fontSize: '1.2rem', fontWeight: 900, color: BRAND.ivory };
const iconBox = { width: 34, height: 34, borderRadius: 10, background: 'rgba(205,155,80,0.14)', border: `1px solid ${BRAND.line}`, display: 'grid', placeItems: 'center', color: BRAND.goldLight, flexShrink: 0 };
const mono = { fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace" };

export default function PersonalStats() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(true);
  const [lbScope, setLbScope] = useState('global'); // 'global' or 'game'
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState('');

  // Load progress + games list once on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/progress/my');
        if (!cancelled) setProgress(data.progress || null);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || 'Unable to load your statistics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    async function loadGames() {
      try {
        // The /me endpoint returns the current user; if course is on the
        // user object it's a populated reference, else just an id.
        const meRes = await api.get('/auth/me');
        const me = meRes.data?.user || meRes.data;
        const courseId = me?.course?._id || me?.course || null;
        if (!courseId) return;
        const { data } = await api.get(`/games/course/${courseId}`);
        if (!cancelled) setGames(data.games || []);
      } catch (e) {
        if (!cancelled) setGames([]);
      }
    }
    load();
    loadGames();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-fetch leaderboard whenever the selected game changes
  useEffect(() => {
    let cancelled = false;
    async function loadLeaderboard() {
      setLbLoading(true);
      try {
        const url = selectedGameId
          ? `/progress/leaderboard?game=${encodeURIComponent(selectedGameId)}`
          : '/progress/leaderboard';
        const { data } = await api.get(url);
        if (!cancelled) {
          setLeaderboard(data.leaderboard || []);
          setLbScope(data.scope || (selectedGameId ? 'game' : 'global'));
        }
      } catch (e) {
        if (!cancelled) setLeaderboard([]);
      } finally {
        if (!cancelled) setLbLoading(false);
      }
    }
    loadLeaderboard();
    return () => { cancelled = true; };
  }, [selectedGameId]);

  const stats = useMemo(() => {
    const p = progress || {};
    const gameProgress = Array.isArray(p.gameProgress) ? p.gameProgress : [];
    // Aggregate across ALL games the student has played:
    //   - sum of scores
    //   - count of completed games
    //   - sum of best times (in ms) for completed games
    let gamePoints = 0;
    let gamesCompleted = 0;
    let totalBestTimeMs = 0;
    for (const g of gameProgress) {
      gamePoints += g.score || 0;
      if (g.completed) gamesCompleted += 1;
      const bt = g && g.data && g.data.bestTime;
      if (typeof bt === 'number' && bt > 0) totalBestTimeMs += bt;
    }
    return {
      gamePoints,
      gamesCompleted,
      gamesTotal: games.length || 0,
      totalBestTimeMs,
    };
  }, [progress, games]);

  // Lesson progress entries (populated with title/order by /progress/my), oldest lesson first
  const lessonRows = useMemo(() => {
    const entries = Array.isArray(progress?.lessons) ? progress.lessons : [];
    return entries
      .map((e, i) => {
        const l = e && typeof e.lesson === 'object' ? e.lesson : null;
        return { key: l?._id || e?.lesson || i, title: l?.title || 'Lesson', order: typeof l?.order === 'number' ? l.order : 9999, entry: e };
      })
      .sort((a, b) => a.order - b.order);
  }, [progress]);

  const badges = useMemo(() => (Array.isArray(progress?.badges) ? progress.badges : []), [progress]);
  const lessonsDone = lessonRows.filter(r => r.entry?.completed).length;
  const streak = progress?.streak || 0;
  const myName = user?.displayName || user?.name || '';

  return (
    <Page hero={HERO}>
      <PageHeader
        kicker="Your progress"
        title="My stats"
        subtitle={myName ? `Nice work, ${firstNameOnly(myName)}. Here is everything you've earned so far.` : 'Badges, streak and your place on the leaderboard.'}
        right={!loading && !error ? <Tag tone="solid"><FiAward size={12} /> {progress?.totalPoints || 0} points</Tag> : null}
      />

      {loading && <LoadingSpinner message="Counting your badges…" />}

      {!loading && error && <Alert>{error}</Alert>}

      {!loading && !error && (
        <>
          {/* ----- Stat tiles ----- */}
          <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
            <motion.div variants={fadeUp}><Stat icon="⭐" value={progress?.totalPoints || 0} label="total points" /></motion.div>
            <motion.div variants={fadeUp}>
              <Stat icon="📘" value={lessonsDone} label="lessons completed">
                {lessonRows.length > 0 && <ProgressBar value={lessonRows.length ? (lessonsDone / lessonRows.length) * 100 : 0} height={4} style={{ marginTop: 8 }} />}
              </Stat>
            </motion.div>
            <motion.div variants={fadeUp}><Stat icon="🏅" value={badges.length} label="badges earned" /></motion.div>
            <motion.div variants={fadeUp}><Stat icon="🔥" value={`${streak} ${streak === 1 ? 'day' : 'days'}`} label="streak" /></motion.div>
            <motion.div variants={fadeUp}>
              <Stat icon="🎮" value={`${stats.gamePoints} pts`} label="games — total">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: '0.78rem', color: BRAND.muted, fontWeight: 700 }}>
                  <span title="Games completed" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <FiCheckCircle size={12} /> {stats.gamesCompleted}{stats.gamesTotal > 0 ? `/${stats.gamesTotal}` : ''} completed
                  </span>
                  <span title="Total best time across completed games" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...mono }}>
                    <FiClock size={12} /> {stats.totalBestTimeMs > 0 ? formatGameTime(stats.totalBestTimeMs) : '—'}
                  </span>
                </div>
              </Stat>
            </motion.div>
          </motion.div>

          {/* ----- Lesson progress ----- */}
          <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 28 }}>
            <header style={sectionHead}>
              <h2 style={sectionTitle}><span style={iconBox}><FiBookOpen size={17} /></span> Lesson progress</h2>
              {lessonRows.length > 0 && <Tag tone="muted">{lessonsDone} of {lessonRows.length} done</Tag>}
            </header>
            {lessonRows.length === 0 ? (
              <EmptyState icon="🎒" title="No lesson started yet" text="Open a lesson with Trace and the team — your progress will show up here." />
            ) : (
              <motion.ul variants={stagger} initial="hidden" animate="visible" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {lessonRows.map((r, i) => {
                  const done = !!r.entry?.completed;
                  const spent = formatMinutes(r.entry?.timeSpent);
                  return (
                    <motion.li key={r.key} variants={fadeUp}>
                      <Card style={{ padding: '14px 16px', height: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          <span aria-hidden style={{ fontSize: '1.5rem', fontWeight: 900, lineHeight: 1, color: 'rgba(205,155,80,0.5)', minWidth: 34 }}>{String(i + 1).padStart(2, '0')}</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <strong style={{ display: 'block', fontSize: '0.98rem', lineHeight: 1.25, color: BRAND.ivory }}>{r.title}</strong>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4, fontSize: '0.74rem', color: BRAND.muted, fontWeight: 700 }}>
                              {spent && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FiClock size={11} /> {spent}</span>}
                              {done && r.entry?.completedAt && <span>Completed {new Date(r.entry.completedAt).toLocaleDateString()}</span>}
                            </div>
                          </div>
                          {done ? <Tag tone="success"><FiCheckCircle size={11} /> Done</Tag> : <Tag tone="solid"><FiPlay size={11} /> In progress</Tag>}
                        </div>
                        <ProgressBar value={lessonPercent(r.entry)} height={5} />
                      </Card>
                    </motion.li>
                  );
                })}
              </motion.ul>
            )}
          </motion.section>

          {/* ----- Lesson badges (earned inside the interactive lessons) ----- */}
          <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 28 }}>
            <header style={sectionHead}>
              <h2 style={sectionTitle}><img src={BRAND.owl} alt="" aria-hidden style={{ width: 34, height: 34, objectFit: 'contain' }} /> Lesson badges</h2>
              <Tag tone="gold">{badges.length} earned</Tag>
            </header>
            {badges.length === 0 ? (
              <EmptyState icon="🏅" title="No badge yet" text="Complete the chapters and missions inside the lessons to earn your first badge." />
            ) : (
              <motion.ul variants={stagger} initial="hidden" animate="visible" style={{ listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, padding: 0, margin: 0 }}>
                {badges.map((b) => (
                  <motion.li key={b.id} variants={fadeUp} title={b.desc || b.name}>
                    <Card hover style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', height: '100%' }}>
                      <span style={{ width: 52, height: 52, borderRadius: '50%', flexShrink: 0, background: 'radial-gradient(circle at 35% 30%, rgba(243,220,180,0.25), rgba(7,20,45,0.9))', border: `2px solid ${BRAND.gold}`, boxShadow: '0 0 0 4px rgba(205,155,80,0.14), 0 8px 22px rgba(0,0,0,0.35)', display: 'grid', placeItems: 'center', fontSize: 24 }}>{b.icon || '🏅'}</span>
                      <div style={{ minWidth: 0 }}>
                        <strong style={{ display: 'block', fontSize: '0.95rem', color: BRAND.ivory, lineHeight: 1.2 }}>{b.name}</strong>
                        {b.desc && <span style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: BRAND.muted, lineHeight: 1.3, marginTop: 2 }}>{b.desc}</span>}
                        {b.earnedAt && <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 800, color: BRAND.goldLight, marginTop: 4, letterSpacing: '0.04em' }}>{new Date(b.earnedAt).toLocaleDateString()}</span>}
                      </div>
                    </Card>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </motion.section>
        </>
      )}

      {/* ----- Game Best Times (server-side gameProgress.data.bestTime first, localStorage as fallback) ----- */}
      {(() => {
        const serverTimes = new Map();
        for (const g of (progress?.gameProgress || [])) {
          const bt = g?.data?.bestTime;
          if (typeof bt === 'number' && bt > 0) {
            const title = games.find((x) => x._id === g.gameId)?.title || g.gameId;
            serverTimes.set(title, bt);
          }
        }
        for (const { game, key } of GAME_BEST_TIME_KEYS) {
          let raw = 0;
          try { raw = parseInt(localStorage.getItem(key) || '0', 10); } catch { raw = 0; }
          if (raw > 0 && (!serverTimes.has(game) || raw < serverTimes.get(game))) serverTimes.set(game, raw);
        }
        const bestTimes = [...serverTimes.entries()].map(([game, ms]) => ({ game, ms }));
        if (bestTimes.length === 0) return null;
        return (
          <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 28 }}>
            <header style={sectionHead}>
              <h2 style={sectionTitle}><span style={iconBox}><FiClock size={17} /></span> Game best times</h2>
            </header>
            <ul style={{ listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, padding: 0, margin: 0 }}>
              {bestTimes.map((b) => (
                <li key={b.game}>
                  <Card style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: '0.74rem', fontWeight: 800, color: BRAND.goldLight, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{b.game}</span>
                    <strong style={{ fontSize: '1.6rem', color: BRAND.ivory, lineHeight: 1.1, ...mono }}>{formatBestTime(b.ms)}</strong>
                  </Card>
                </li>
              ))}
            </ul>
          </motion.section>
        );
      })()}

      {/* ----- Global Leaderboard (all students across all courses) ----- */}
      <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 28 }}>
        <header style={sectionHead}>
          <h2 style={sectionTitle}><span style={iconBox}><FiUsers size={17} /></span> Leaderboard</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label htmlFor="lb-game-filter" style={{ ...labelStyle(false), margin: 0 }}>Filter</label>
            <select
              id="lb-game-filter"
              value={selectedGameId}
              onChange={(e) => setSelectedGameId(e.target.value)}
              style={{ ...inputStyle(false), width: 'auto', minWidth: 200, padding: '9px 36px 9px 12px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer', borderRadius: 999, borderColor: BRAND.line, appearance: 'none', WebkitAppearance: 'none', backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='%23e2b878' stroke-width='2' stroke-linecap='round'/></svg>")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}
            >
              <option value="">All games (total points)</option>
              {games.map((g) => (
                <option key={g._id} value={g._id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
        </header>

        <Card style={{ padding: '6px 0' }}>
          {lbLoading && (
            <p style={{ padding: '14px 20px', margin: 0, fontWeight: 700, color: BRAND.muted, fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.05rem' }}>
              Loading the leaderboard…
            </p>
          )}

          {!lbLoading && leaderboard.length === 0 && (
            <p style={{ padding: '14px 20px', margin: 0, fontWeight: 600, color: BRAND.muted }}>
              {lbScope === 'game'
                ? 'No one has played this game yet — be the first!'
                : 'No scores yet — be the first to play and earn points!'}
            </p>
          )}

          {!lbLoading && leaderboard.length > 0 && (
            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li aria-hidden className="core-lb-row" style={{ display: 'grid', gridTemplateColumns: '44px minmax(0,1fr) 90px 110px', gap: 12, alignItems: 'center', padding: '8px 20px 10px', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: BRAND.goldLight, borderBottom: `1px solid ${BRAND.lineSoft}` }}>
                <span>#</span>
                <span>Student</span>
                <span className="core-lb-course">Course</span>
                <span style={{ textAlign: 'right' }}>{lbScope === 'game' ? 'Score · Best time' : 'Points'}</span>
              </li>
              {leaderboard.map((row) => {
                const isTop3 = row.rank <= 3;
                const medal = row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : null;
                const isMe = !!myName && row.displayName === myName;
                return (
                  <li
                    key={row.rank + '-' + row.displayName}
                    className="core-lb-row"
                    style={{
                      display: 'grid', gridTemplateColumns: '44px minmax(0,1fr) 90px 110px', gap: 12, alignItems: 'center',
                      padding: '11px 20px',
                      borderTop: `1px solid ${BRAND.lineSoft}`,
                      background: isMe ? 'linear-gradient(90deg, rgba(205,155,80,0.22), rgba(205,155,80,0.06))' : isTop3 ? 'rgba(205,155,80,0.05)' : 'transparent',
                      boxShadow: isMe ? `inset 3px 0 0 ${BRAND.gold}` : 'none',
                    }}
                  >
                    <span style={{ fontWeight: 900, fontSize: medal ? '1.25rem' : '0.95rem', color: isMe ? BRAND.goldLight : BRAND.ivory, textAlign: 'center' }}>
                      {medal || row.rank}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <Initials name={row.displayName} size={32} style={isMe ? { boxShadow: `0 0 0 2px ${BRAND.navy900}, 0 0 0 4px ${BRAND.gold}` } : null} />
                      <span style={{ fontWeight: 800, color: isMe ? BRAND.goldLight : BRAND.ivory, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firstNameOnly(row.displayName)}</span>
                      {isMe && <Tag tone="solid" style={{ padding: '2px 8px', fontSize: '0.62rem' }}>You</Tag>}
                    </span>
                    <span className="core-lb-course"><Tag tone="muted">{courseLabel(row.course)}</Tag></span>
                    {lbScope === 'game' ? (
                      <span style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.15 }}>
                        <b style={{ fontWeight: 900, color: BRAND.goldLight, fontSize: '1rem' }}>{row.score || 0}</b>
                        <span style={{ fontSize: '0.74rem', color: BRAND.muted, fontWeight: 700, ...mono }}>{formatGameTime(row.bestTime)}</span>
                      </span>
                    ) : (
                      <span style={{ textAlign: 'right', fontWeight: 900, color: BRAND.goldLight, fontSize: '1rem' }}>
                        {row.totalPoints} <span style={{ fontSize: '0.7rem', color: BRAND.muted, fontWeight: 700 }}>pts</span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </Card>
      </motion.section>

      <style>{`
        @media (max-width: 560px) {
          .core-lb-row { grid-template-columns: 36px minmax(0,1fr) 90px !important; padding-left: 14px !important; padding-right: 14px !important; }
          .core-lb-course { display: none !important; }
        }
      `}</style>
    </Page>
  );
}
